import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  CreditCard,
  FlaskConical,
  Home,
  Layers,
  type LucideIcon,
  Radio,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  Ticket,
  Users,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { MOCK_COHORTS, MOCK_PROGRAMS } from "@/components/mocks/fixtures";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface PaletteItem {
  id: string;
  group: string;
  label: string;
  detail?: string;
  icon: LucideIcon;
  run: () => void;
  keywords?: string;
}

/**
 * Linear/Raycast-style command palette. Mounted at the AppLayout level —
 * toggled by ⌘K / Ctrl-K. Pure navigation in v1; mutating actions come
 * with the Phase B API.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Build the command list. Memoized — only changes when fixtures change.
  const allItems = useMemo<PaletteItem[]>(() => {
    const go = (to: string) => () => {
      navigate(to);
      onClose();
    };
    const nav: PaletteItem[] = [
      { id: "nav-home", group: "Navigate", label: "Home", icon: Home, run: go("/") },
      { id: "nav-cohorts", group: "Navigate", label: "Cohorts", icon: Layers, run: go("/cohorts") },
      { id: "nav-programs", group: "Navigate", label: "Programs", icon: Sparkles, run: go("/programs") },
      { id: "nav-analytics", group: "Navigate", label: "Analytics", icon: BarChart3, run: go("/analytics") },
      { id: "nav-codes", group: "Navigate", label: "Invite codes", icon: Ticket, run: go("/codes") },
      { id: "nav-outreach", group: "Navigate", label: "Outreach feed", icon: Radio, run: go("/outreach") },
      { id: "nav-sandbox", group: "Navigate", label: "Sandbox", icon: FlaskConical, run: go("/sandbox") },
      { id: "nav-team", group: "Navigate", label: "Team", icon: Users, run: go("/team") },
      { id: "nav-billing", group: "Navigate", label: "Billing", icon: CreditCard, run: go("/billing") },
      { id: "nav-settings", group: "Navigate", label: "Settings", icon: SettingsIcon, run: go("/settings") },
    ];
    const programs: PaletteItem[] = MOCK_PROGRAMS.map((p) => ({
      id: `prog-${p.id}`,
      group: "Programs",
      label: p.name,
      detail: `v${p.version} · ${p.status}`,
      icon: Sparkles,
      keywords: p.description,
      run: go(`/programs/${p.id}`),
    }));
    const cohorts: PaletteItem[] = MOCK_COHORTS.map((c) => ({
      id: `coh-${c.id}`,
      group: "Cohorts",
      label: c.name,
      detail: `${c.memberCount.toLocaleString()} members · ${c.status}`,
      icon: Layers,
      keywords: c.programName,
      run: go(`/cohorts/${c.id}`),
    }));
    return [...nav, ...programs, ...cohorts];
  }, [navigate, onClose]);

  // Filtered, ordered list based on query.
  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((it) => {
      const hay = `${it.label} ${it.group} ${it.detail ?? ""} ${it.keywords ?? ""}`.toLowerCase();
      return q.split(/\s+/).every((tok) => hay.includes(tok));
    });
  }, [allItems, query]);

  // Reset state when opening; focus the input.
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      window.setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Keep the active idx in view when arrowing.
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLLIElement>(
      `[data-idx="${activeIdx}"]`,
    );
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  // Reset active idx when items shrink.
  useEffect(() => {
    if (activeIdx > items.length - 1) setActiveIdx(0);
  }, [items.length, activeIdx]);

  // Global ⌘K toggle is handled by parent. Local handlers: ESC + arrow + enter.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        items[activeIdx]?.run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, items, activeIdx, onClose]);

  // Body scroll lock.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  // Group items for rendering, preserving order within each group.
  const grouped: { group: string; items: PaletteItem[]; firstIdx: number }[] =
    [];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const last = grouped[grouped.length - 1];
    if (last && last.group === it.group) {
      last.items.push(it);
    } else {
      grouped.push({ group: it.group, items: [it], firstIdx: i });
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4"
    >
      <div
        aria-hidden
        onClick={onClose}
        className="absolute inset-0 bg-[rgba(4,5,6,0.78)]"
        style={{ backdropFilter: "blur(8px) saturate(160%)" }}
      />

      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[560px] rounded-[var(--radius-modal)] shadow-xl-eon anim-fade-up overflow-hidden"
        style={{
          background: "var(--color-deep-charcoal)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 h-12 border-b border-[var(--color-graphite-500)]/40">
          <Search size={16} className="text-[var(--color-slate-300)]" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search anything…"
            className="flex-1 bg-transparent outline-none text-[15px] text-[var(--color-snow)] placeholder:text-[var(--color-slate-300)]"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="font-mono text-[10px] tracking-[0.06em] uppercase text-[var(--color-slate-300)] px-1.5 py-0.5 rounded shadow-hairline">
            ESC
          </kbd>
        </div>

        {/* Results */}
        {items.length === 0 ? (
          <p className="px-4 py-8 text-center text-[13px] text-[var(--color-slate-300)]">
            No matches for "{query}".
          </p>
        ) : (
          <ul ref={listRef} className="max-h-[60vh] overflow-y-auto py-1">
            {grouped.map((g) => (
              <li key={g.group}>
                <p className="px-4 pt-3 pb-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--color-slate-300)]">
                  {g.group}
                </p>
                <ul>
                  {g.items.map((it, j) => {
                    const idx = g.firstIdx + j;
                    const Icon = it.icon;
                    return (
                      <li
                        key={it.id}
                        data-idx={idx}
                        onMouseEnter={() => setActiveIdx(idx)}
                        onClick={() => it.run()}
                        className={cn(
                          "mx-1 px-3 py-2 rounded-[var(--radius-button)] cursor-pointer",
                          "flex items-center gap-2.5 text-[14px]",
                          idx === activeIdx
                            ? "bg-[var(--color-graphite-700)] text-[var(--color-snow)] shadow-ring"
                            : "text-[var(--color-slate-200)]",
                        )}
                      >
                        <Icon size={14} className="opacity-80 shrink-0" />
                        <span className="flex-1 min-w-0 truncate">{it.label}</span>
                        {it.detail && (
                          <span className="font-mono text-[11px] text-[var(--color-slate-300)] shrink-0">
                            {it.detail}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}

        {/* Footer hint */}
        <Footer />
      </div>
    </div>
  );
}

function Footer() {
  return (
    <div className="flex items-center justify-between gap-3 px-4 h-8 border-t border-[var(--color-graphite-500)]/40 text-[var(--color-slate-300)] font-mono text-[10px] uppercase tracking-[0.06em]">
      <span className="inline-flex items-center gap-2">
        <KbdGroup>↑↓</KbdGroup>
        navigate
      </span>
      <span className="inline-flex items-center gap-2">
        <KbdGroup>↵</KbdGroup>
        open
      </span>
      <span className="inline-flex items-center gap-2">
        <KbdGroup>⌘K</KbdGroup>
        toggle
      </span>
    </div>
  );
}

function KbdGroup({ children }: { children: ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded shadow-hairline text-[10px] text-[var(--color-slate-200)]">
      {children}
    </kbd>
  );
}
